import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Eye, Edit2, Trash2, CheckCircle2, XCircle, Clock,
  FileText, X, ChevronsUpDown, Check, DollarSign, RefreshCw, ArrowRightLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../../../components/ui/command";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = {
  BORRADOR: { label: "Borrador", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  CONFIRMADO: { label: "Confirmado", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  ANULADO: { label: "Anulado", color: "bg-red-100 text-red-700", icon: XCircle },
};

const TIPOS_ASIENTO = [
  { value: "DIARIO", label: "Diario" },
  { value: "APERTURA", label: "Apertura" },
  { value: "CIERRE", label: "Cierre" },
  { value: "AJUSTE_INFLACION", label: "Ajuste Inflacion RT54" },
];

const CuentaCombobox = ({ cuentas, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const selectedCuenta = cuentas.find(c => c.id.toString() === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
          className="w-full justify-between text-left font-normal h-9 px-3" data-testid="cuenta-combobox-trigger">
          <span className="truncate">
            {selectedCuenta ? `${selectedCuenta.codigo} - ${selectedCuenta.descripcion}` : "Buscar cuenta..."}
          </span>
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar por codigo o nombre..." />
          <CommandList>
            <CommandEmpty>No se encontraron cuentas.</CommandEmpty>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {cuentas.map((c) => (
                <CommandItem key={c.id} value={`${c.codigo} ${c.descripcion}`}
                  onSelect={() => { onChange(c.id.toString()); setOpen(false); }}>
                  <Check className={"mr-2 h-4 w-4 " + (value === c.id.toString() ? "opacity-100" : "opacity-0")} />
                  <span className="font-mono text-xs mr-2">{c.codigo}</span>
                  <span className="truncate">{c.descripcion}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const LINEA_VACIA = { cuenta_id: "", centro_costo_id: "", moneda_id: "1", importe_moneda_origen_debe: 0, importe_moneda_origen_haber: 0, tipo_cambio: 1, debe: 0, haber: 0, concepto_linea: "" };

const Asientos = ({ tenantId, ejercicioId }) => {
  const [asientos, setAsientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("crear");
  const [asientoEditando, setAsientoEditando] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [difCambioOpen, setDifCambioOpen] = useState(false);
  const [difCambioFecha, setDifCambioFecha] = useState(new Date().toISOString().split("T")[0]);
  const [difCambioLoading, setDifCambioLoading] = useState(false);
  const [difCambioResult, setDifCambioResult] = useState(null);
  
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split("T")[0],
    concepto: "", tipo: "DIARIO", comprobante_tipo: "", comprobante_numero: "",
    detalles: [{ ...LINEA_VACIA }, { ...LINEA_VACIA }],
  });

  const fetchAsientos = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/erp/empresas/${tenantId}/ejercicios/${ejercicioId}/asientos`;
      if (filtroEstado !== "todos") url += `?estado=${filtroEstado}`;
      const response = await axios.get(url);
      setAsientos(response.data.asientos || []);
    } catch (error) {
      toast.error("Error al cargar asientos");
    } finally { setLoading(false); }
  };

  const fetchCuentas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${tenantId}/cuentas?solo_imputables=true`);
      setCuentas(response.data);
    } catch (error) { console.error("Error:", error); }
  };

  const fetchCentrosCosto = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${tenantId}/centros-costo`);
      setCentrosCosto(response.data);
    } catch (error) { console.error("Error:", error); }
  };

  const fetchMonedas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/monedas`);
      setMonedas(response.data);
    } catch (error) { console.error("Error:", error); }
  };

  useEffect(() => {
    fetchAsientos(); fetchCuentas(); fetchCentrosCosto(); fetchMonedas();
  }, [tenantId, ejercicioId, filtroEstado]);

  const fetchTipoCambio = async (monedaId, fecha) => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${tenantId}/tipo-cambio/${monedaId}?fecha=${fecha}`);
      return response.data.tipo_cambio;
    } catch { return 1; }
  };

  const handleOpenModal = async (mode, asiento = null) => {
    setModalMode(mode);
    setViewMode(mode === "ver");
    if (asiento && (mode === "ver" || mode === "editar")) {
      try {
        const response = await axios.get(`${API_URL}/api/erp/empresas/${tenantId}/asientos/${asiento.id}`);
        const data = response.data;
        setAsientoEditando(data);
        setFormData({
          fecha: data.fecha, concepto: data.concepto, tipo: data.tipo,
          comprobante_tipo: data.comprobante_tipo || "", comprobante_numero: data.comprobante_numero || "",
          detalles: data.detalles.map(d => ({
            cuenta_codigo: d.cuenta_codigo || "", cuenta_descripcion: d.cuenta_descripcion || "",
            cuenta_id: d.cuenta_id.toString(),
            centro_costo_id: d.centro_costo_id ? d.centro_costo_id.toString() : "",
            centro_costo_descripcion: d.centro_costo_descripcion || "",
            moneda_id: (d.moneda_id || 1).toString(),
            moneda_codigo: d.moneda_codigo || "ARS",
            importe_moneda_origen_debe: parseFloat(d.importe_moneda_origen_debe) || 0,
            importe_moneda_origen_haber: parseFloat(d.importe_moneda_origen_haber) || 0,
            tipo_cambio: parseFloat(d.tipo_cambio) || 1,
            debe: parseFloat(d.debe) || 0, haber: parseFloat(d.haber) || 0,
            concepto_linea: d.concepto_linea || "",
          })),
        });
      } catch (error) { toast.error("Error al cargar el asiento"); return; }
    } else {
      setAsientoEditando(null);
      setFormData({
        fecha: new Date().toISOString().split("T")[0], concepto: "", tipo: "DIARIO",
        comprobante_tipo: "", comprobante_numero: "",
        detalles: [{ ...LINEA_VACIA }, { ...LINEA_VACIA }],
      });
    }
    setModalOpen(true);
  };

  const addLinea = () => {
    setFormData({ ...formData, detalles: [...formData.detalles, { ...LINEA_VACIA }] });
  };

  const removeLinea = (index) => {
    if (formData.detalles.length <= 2) { toast.error("Minimo 2 lineas"); return; }
    setFormData({ ...formData, detalles: formData.detalles.filter((_, i) => i !== index) });
  };

  const updateLinea = (index, field, value) => {
    const newDetalles = [...formData.detalles];
    newDetalles[index][field] = value;
    setFormData({ ...formData, detalles: newDetalles });
  };

  const handleMonedaChange = async (index, monedaId) => {
    const newDetalles = [...formData.detalles];
    newDetalles[index].moneda_id = monedaId;
    if (monedaId !== "1") {
      const tc = await fetchTipoCambio(monedaId, formData.fecha);
      newDetalles[index].tipo_cambio = tc;
      // Recalcular importes funcionales
      newDetalles[index].debe = Math.round(newDetalles[index].importe_moneda_origen_debe * tc * 100) / 100;
      newDetalles[index].haber = Math.round(newDetalles[index].importe_moneda_origen_haber * tc * 100) / 100;
    } else {
      newDetalles[index].tipo_cambio = 1;
    }
    setFormData({ ...formData, detalles: newDetalles });
  };

  const handleOrigenChange = (index, field, value) => {
    const newDetalles = [...formData.detalles];
    const numValue = parseFloat(value) || 0;
    newDetalles[index][field] = numValue;
    const tc = newDetalles[index].tipo_cambio || 1;
    if (field === "importe_moneda_origen_debe") {
      newDetalles[index].debe = Math.round(numValue * tc * 100) / 100;
    } else {
      newDetalles[index].haber = Math.round(numValue * tc * 100) / 100;
    }
    setFormData({ ...formData, detalles: newDetalles });
  };

  const handleTcChange = (index, value) => {
    const newDetalles = [...formData.detalles];
    const tc = parseFloat(value) || 1;
    newDetalles[index].tipo_cambio = tc;
    newDetalles[index].debe = Math.round(newDetalles[index].importe_moneda_origen_debe * tc * 100) / 100;
    newDetalles[index].haber = Math.round(newDetalles[index].importe_moneda_origen_haber * tc * 100) / 100;
    setFormData({ ...formData, detalles: newDetalles });
  };

  const calcularTotales = () => {
    const totalDebe = formData.detalles.reduce((sum, d) => sum + (parseFloat(d.debe) || 0), 0);
    const totalHaber = formData.detalles.reduce((sum, d) => sum + (parseFloat(d.haber) || 0), 0);
    return { totalDebe, totalHaber, diferencia: totalDebe - totalHaber };
  };

  const handleSave = async () => {
    const { totalDebe, totalHaber } = calcularTotales();
    if (!formData.concepto) { toast.error("Ingrese un concepto"); return; }
    if (Math.abs(totalDebe - totalHaber) > 0.01) {
      toast.error(`El asiento no balancea. Diferencia: ${(totalDebe - totalHaber).toFixed(2)}`);
      return;
    }
    const detallesValidos = formData.detalles.filter(d => d.cuenta_id && (d.debe > 0 || d.haber > 0));
    if (detallesValidos.length < 2) { toast.error("Minimo 2 lineas con movimientos"); return; }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        detalles: detallesValidos.map(d => ({
          cuenta_id: parseInt(d.cuenta_id),
          centro_costo_id: d.centro_costo_id ? parseInt(d.centro_costo_id) : null,
          moneda_id: parseInt(d.moneda_id) || 1,
          importe_moneda_origen_debe: parseFloat(d.importe_moneda_origen_debe) || 0,
          importe_moneda_origen_haber: parseFloat(d.importe_moneda_origen_haber) || 0,
          tipo_cambio: parseFloat(d.tipo_cambio) || 1,
          debe: parseFloat(d.debe) || 0,
          haber: parseFloat(d.haber) || 0,
          concepto_linea: d.concepto_linea,
        })),
      };
      if (modalMode === "crear") {
        await axios.post(`${API_URL}/api/erp/empresas/${tenantId}/ejercicios/${ejercicioId}/asientos`, payload);
        toast.success("Asiento creado");
      } else {
        await axios.put(`${API_URL}/api/erp/empresas/${tenantId}/asientos/${asientoEditando.id}`, payload);
        toast.success("Asiento actualizado");
      }
      setModalOpen(false);
      fetchAsientos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleConfirmar = async (asiento) => {
    try {
      await axios.put(`${API_URL}/api/erp/empresas/${tenantId}/asientos/${asiento.id}/confirmar?usuario_id=admin`);
      toast.success("Asiento confirmado"); fetchAsientos();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al confirmar"); }
  };

  const handleAnular = async (asiento) => {
    const motivo = prompt("Motivo de la anulacion:");
    if (!motivo) return;
    try {
      await axios.put(`${API_URL}/api/erp/empresas/${tenantId}/asientos/${asiento.id}/anular?motivo=${encodeURIComponent(motivo)}&usuario_id=admin`);
      toast.success("Asiento anulado"); fetchAsientos();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al anular"); }
  };

  const handleEliminar = async (asiento) => {
    if (!confirm("Eliminar este asiento?")) return;
    try {
      await axios.delete(`${API_URL}/api/erp/empresas/${tenantId}/asientos/${asiento.id}`);
      toast.success("Asiento eliminado"); fetchAsientos();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al eliminar"); }
  };

  const handleDifCambio = async () => {
    setDifCambioLoading(true);
    setDifCambioResult(null);
    try {
      const url = API_URL + "/api/erp/empresas/" + tenantId + "/ejercicios/" + ejercicioId + "/diferencia-cambio?fecha_cierre=" + difCambioFecha;
      const response = await axios.post(url);
      setDifCambioResult(response.data);
      if (response.data.asiento_id) {
        toast.success("Asiento #" + response.data.asiento_numero + " generado");
        fetchAsientos();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar diferencia de cambio");
    } finally { setDifCambioLoading(false); }
  };

  const formatCurrency = (value) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(value);
  const formatFecha = (fecha) => new Date(fecha).toLocaleDateString("es-AR");
  
  // Determinar si alguna linea usa moneda extranjera
  const tieneMonedaExtranjera = formData.detalles.some(d => d.moneda_id && d.moneda_id !== "1");
  const totales = calcularTotales();

  return (
    <div className="space-y-4">
      <Card className="cao-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-2">
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="w-40" data-testid="filtro-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="BORRADOR">Borrador</SelectItem>
                  <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
                  <SelectItem value="ANULADO">Anulado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDifCambioOpen(true)} data-testid="btn-dif-cambio">
                <ArrowRightLeft className="w-4 h-4 mr-2" />Dif. Cambio
              </Button>
              <Button onClick={() => handleOpenModal("crear")} data-testid="btn-nuevo-asiento">
                <Plus className="w-4 h-4 mr-2" />Nuevo Asiento
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="cao-card">
        <CardHeader className="pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-[#1E3876]">Asientos Contables</CardTitle>
            <span className="text-sm text-gray-500">{asientos.length} asientos</span>
          </div>
        </CardHeader>
        <CardContent className="p-0 mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
            </div>
          ) : asientos.length === 0 ? (
            <div className="text-center py-12"><FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">No hay asientos registrados</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Nro</TableHead>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead>Concepto</TableHead>
                    <TableHead className="text-right">Debe</TableHead>
                    <TableHead className="text-right">Haber</TableHead>
                    <TableHead className="w-32">Estado</TableHead>
                    <TableHead className="w-32">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {asientos.map((asiento) => {
                    const estadoConfig = ESTADOS[asiento.estado];
                    const IconEstado = estadoConfig?.icon || Clock;
                    return (
                      <TableRow key={asiento.id}>
                        <TableCell className="font-mono">{asiento.numero}</TableCell>
                        <TableCell>{formatFecha(asiento.fecha)}</TableCell>
                        <TableCell className="max-w-xs truncate">{asiento.concepto}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(asiento.total_debe)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(asiento.total_haber)}</TableCell>
                        <TableCell>
                          <Badge className={`${estadoConfig?.color} flex items-center gap-1 w-fit`}>
                            <IconEstado className="w-3 h-3" />{estadoConfig?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenModal("ver", asiento)}><Eye className="w-4 h-4" /></Button>
                            {asiento.estado === "BORRADOR" && (
                              <>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenModal("editar", asiento)}><Edit2 className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => handleConfirmar(asiento)} title="Confirmar"><CheckCircle2 className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleEliminar(asiento)}><Trash2 className="w-4 h-4" /></Button>
                              </>
                            )}
                            {asiento.estado === "CONFIRMADO" && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleAnular(asiento)} title="Anular"><XCircle className="w-4 h-4" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar/Ver Asiento */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">
              {viewMode ? `Asiento #${asientoEditando?.numero}` : modalMode === "crear" ? "Nuevo Asiento" : "Editar Asiento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Fecha *</Label><Input type="date" value={formData.fecha} onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} disabled={viewMode} /></div>
              <div><Label>Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })} disabled={viewMode}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_ASIENTO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tipo Comp.</Label><Input value={formData.comprobante_tipo} onChange={(e) => setFormData({ ...formData, comprobante_tipo: e.target.value })} placeholder="FC, RC..." disabled={viewMode} /></div>
              <div><Label>Nro Comp.</Label><Input value={formData.comprobante_numero} onChange={(e) => setFormData({ ...formData, comprobante_numero: e.target.value })} placeholder="0001-00000001" disabled={viewMode} /></div>
            </div>
            <div><Label>Concepto *</Label><Input value={formData.concepto} onChange={(e) => setFormData({ ...formData, concepto: e.target.value })} placeholder="Descripcion del asiento" disabled={viewMode} /></div>

            {/* Detalle del Asiento */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="flex items-center gap-2">
                  Detalle del Asiento
                  {tieneMonedaExtranjera && <Badge className="bg-blue-100 text-blue-700"><DollarSign className="w-3 h-3 mr-1" />Bimonetario</Badge>}
                </Label>
                {!viewMode && <Button variant="outline" size="sm" onClick={addLinea}><Plus className="w-4 h-4 mr-1" /> Linea</Button>}
              </div>
              
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[240px]">Cuenta</TableHead>
                      {centrosCosto.length > 0 && <TableHead className="w-[130px]">CC</TableHead>}
                      <TableHead className="w-[90px]">Moneda</TableHead>
                      <TableHead className="w-[110px] text-right">Origen Debe</TableHead>
                      <TableHead className="w-[110px] text-right">Origen Haber</TableHead>
                      <TableHead className="w-[80px] text-right">TC</TableHead>
                      <TableHead className="w-[110px] text-right bg-blue-50">Debe ARS</TableHead>
                      <TableHead className="w-[110px] text-right bg-blue-50">Haber ARS</TableHead>
                      <TableHead>Detalle</TableHead>
                      {!viewMode && <TableHead className="w-10"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.detalles.map((linea, index) => {
                      const esMonedaExtranjera = linea.moneda_id && linea.moneda_id !== "1";
                      return (
                        <TableRow key={index} className={esMonedaExtranjera ? "bg-blue-50/30" : ""}>
                          <TableCell>
                            {viewMode ? (
                              <span className="text-sm">{linea.cuenta_codigo} - {linea.cuenta_descripcion}</span>
                            ) : (
                              <CuentaCombobox cuentas={cuentas} value={linea.cuenta_id} onChange={(v) => updateLinea(index, "cuenta_id", v)} disabled={viewMode} />
                            )}
                          </TableCell>
                          {centrosCosto.length > 0 && (
                            <TableCell>
                              {viewMode ? <span className="text-sm">{linea.centro_costo_descripcion || "-"}</span> : (
                                <Select value={linea.centro_costo_id || "none"} onValueChange={(v) => updateLinea(index, "centro_costo_id", v === "none" ? "" : v)}>
                                  <SelectTrigger className="h-9"><SelectValue placeholder="-" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Sin CC</SelectItem>
                                    {centrosCosto.map(cc => <SelectItem key={cc.id} value={cc.id.toString()}>{cc.codigo}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            {viewMode ? (
                              <Badge variant="outline" className="text-xs">{linea.moneda_codigo || "ARS"}</Badge>
                            ) : (
                              <Select value={linea.moneda_id || "1"} onValueChange={(v) => handleMonedaChange(index, v)}>
                                <SelectTrigger className="h-9 w-[80px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {monedas.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.codigo}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>
                            {esMonedaExtranjera ? (
                              <Input type="number" step="0.01" min="0" className="text-right h-9"
                                value={linea.importe_moneda_origen_debe || ""}
                                onChange={(e) => handleOrigenChange(index, "importe_moneda_origen_debe", e.target.value)}
                                disabled={viewMode} />
                            ) : (
                              <span className="text-xs text-gray-400 block text-right">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {esMonedaExtranjera ? (
                              <Input type="number" step="0.01" min="0" className="text-right h-9"
                                value={linea.importe_moneda_origen_haber || ""}
                                onChange={(e) => handleOrigenChange(index, "importe_moneda_origen_haber", e.target.value)}
                                disabled={viewMode} />
                            ) : (
                              <span className="text-xs text-gray-400 block text-right">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {esMonedaExtranjera ? (
                              <Input type="number" step="0.0001" min="0" className="text-right h-9 w-[80px]"
                                value={linea.tipo_cambio || ""}
                                onChange={(e) => handleTcChange(index, e.target.value)}
                                disabled={viewMode} />
                            ) : (
                              <span className="text-xs text-gray-400 block text-right">1</span>
                            )}
                          </TableCell>
                          <TableCell className="bg-blue-50/50">
                            {esMonedaExtranjera ? (
                              <span className="text-right font-mono text-sm block">{formatCurrency(linea.debe)}</span>
                            ) : (
                              <Input type="number" step="0.01" min="0" className="text-right h-9"
                                value={linea.debe || ""}
                                onChange={(e) => updateLinea(index, "debe", parseFloat(e.target.value) || 0)}
                                disabled={viewMode} />
                            )}
                          </TableCell>
                          <TableCell className="bg-blue-50/50">
                            {esMonedaExtranjera ? (
                              <span className="text-right font-mono text-sm block">{formatCurrency(linea.haber)}</span>
                            ) : (
                              <Input type="number" step="0.01" min="0" className="text-right h-9"
                                value={linea.haber || ""}
                                onChange={(e) => updateLinea(index, "haber", parseFloat(e.target.value) || 0)}
                                disabled={viewMode} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Input value={linea.concepto_linea || ""} onChange={(e) => updateLinea(index, "concepto_linea", e.target.value)} placeholder="Detalle" disabled={viewMode} className="h-9" />
                          </TableCell>
                          {!viewMode && (
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeLinea(index)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-gray-50 font-bold">
                      <TableCell colSpan={centrosCosto.length > 0 ? 6 : 5}>TOTALES (Moneda Funcional ARS)</TableCell>
                      <TableCell className="text-right bg-blue-50/50">{formatCurrency(totales.totalDebe)}</TableCell>
                      <TableCell className="text-right bg-blue-50/50">{formatCurrency(totales.totalHaber)}</TableCell>
                      <TableCell colSpan={viewMode ? 1 : 2}>
                        {Math.abs(totales.diferencia) > 0.01 && (
                          <span className="text-red-600 text-sm">Dif: {formatCurrency(Math.abs(totales.diferencia))}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>{viewMode ? "Cerrar" : "Cancelar"}</Button>
            {!viewMode && (
              <Button onClick={handleSave} disabled={saving || Math.abs(totales.diferencia) > 0.01} data-testid="btn-guardar-asiento">
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Diferencia de Cambio */}
      <Dialog open={difCambioOpen} onOpenChange={setDifCambioOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876] flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Diferencia de Cambio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Genera un asiento automatico de diferencia de cambio revaluando los saldos en moneda extranjera
              al tipo de cambio de cierre seleccionado.
            </p>
            <div className="flex items-end gap-4">
              <div>
                <Label>Fecha de Cierre</Label>
                <Input type="date" value={difCambioFecha} onChange={(e) => setDifCambioFecha(e.target.value)}
                  className="w-48" data-testid="dif-cambio-fecha" />
              </div>
              <Button onClick={handleDifCambio} disabled={difCambioLoading} data-testid="btn-generar-dif-cambio">
                {difCambioLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Calculando...</>
                ) : (
                  <><DollarSign className="w-4 h-4 mr-2" />Generar Asiento</>
                )}
              </Button>
            </div>
            {difCambioResult && (
              <div className="space-y-3 mt-4">
                {difCambioResult.asiento_id ? (
                  <>
                    <Badge className="bg-green-100 text-green-700">Asiento #{difCambioResult.asiento_numero} generado</Badge>
                    <div className="text-sm space-y-1">
                      <p><strong>TC Cierre:</strong> {formatCurrency(difCambioResult.tc_cierre)}</p>
                      <p><strong>Diferencia Total:</strong> <span className={difCambioResult.total_diferencia >= 0 ? "text-green-600" : "text-red-600"}>
                        {formatCurrency(Math.abs(difCambioResult.total_diferencia))} ({difCambioResult.total_diferencia >= 0 ? "Ganancia" : "Perdida"})
                      </span></p>
                    </div>
                    <div className="border rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Cuenta</TableHead>
                            <TableHead className="text-right">Saldo USD</TableHead>
                            <TableHead className="text-right">Saldo ARS Actual</TableHead>
                            <TableHead className="text-right">Saldo Revaluado</TableHead>
                            <TableHead className="text-right">Diferencia</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {difCambioResult.detalles.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm">{d.codigo} - {d.descripcion}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(d.saldo_usd)}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(d.saldo_ars_actual)}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(d.saldo_revaluado)}</TableCell>
                              <TableCell className={"text-right font-mono " + (d.diferencia >= 0 ? "text-green-600" : "text-red-600")}>
                                {formatCurrency(Math.abs(d.diferencia))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-700">{difCambioResult.message}</Badge>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDifCambioOpen(false); setDifCambioResult(null); }}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Asientos;
